'use client'

import { useEffect, useState } from 'react'

type AnimatedNumberProps = {
  end: number
  className?: string
  duration?: number
  delay?: number
  decimals?: number
  prefix?: string
  suffix?: string
  compact?: boolean
  signed?: boolean
  loop?: boolean
  loopPause?: number
}

function formatValue(
  value: number,
  decimals: number,
  prefix: string,
  suffix: string,
  compact: boolean,
  signed: boolean
) {
  const abs = Math.abs(value)
  const sign = signed && value > 0 ? '+' : ''
  if (compact && abs >= 1000) {
    const compacted = abs / 1000
    const shown = decimals > 0 ? compacted.toFixed(decimals) : Math.round(compacted).toString()
    return `${sign}${prefix}${shown}${suffix}`
  }
  const shown = decimals > 0 ? abs.toFixed(decimals) : Math.round(abs).toLocaleString('en-US')
  return `${sign}${prefix}${shown}${suffix}`
}

const AnimatedNumber = ({
  end,
  className,
  duration = 1400,
  delay = 0,
  decimals = 0,
  prefix = '',
  suffix = '',
  compact = false,
  signed = false,
  loop = false,
  loopPause = 3500,
}: AnimatedNumberProps) => {
  const [display, setDisplay] = useState(0)

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) {
      setDisplay(end)
      return
    }

    let frame = 0
    let startTime: number | null = null
    let startTimeoutId: ReturnType<typeof setTimeout> | undefined
    let loopTimeoutId: ReturnType<typeof setTimeout> | undefined

    const runCountUp = () => {
      setDisplay(0)
      startTime = null

      const animate = (timestamp: number) => {
        if (startTime === null) startTime = timestamp
        const elapsed = timestamp - startTime
        const progress = Math.min(elapsed / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3)
        setDisplay(end * eased)

        if (progress < 1) {
          frame = requestAnimationFrame(animate)
          return
        }

        if (loop) {
          loopTimeoutId = setTimeout(runCountUp, loopPause)
        }
      }

      frame = requestAnimationFrame(animate)
    }

    startTimeoutId = setTimeout(runCountUp, delay)

    return () => {
      clearTimeout(startTimeoutId)
      clearTimeout(loopTimeoutId)
      cancelAnimationFrame(frame)
    }
  }, [end, duration, delay, loop, loopPause])

  return (
    <span className={className} aria-label={formatValue(end, decimals, prefix, suffix, compact, signed)}>
      {formatValue(display, decimals, prefix, suffix, compact, signed)}
    </span>
  )
}

export default AnimatedNumber
